import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Wifi,
  Zap,
  Gauge,
  Check,
  Phone,
  ArrowLeft,
  Gamepad2,
  Monitor,
  Video,
  Cloud,
  Shield,
  Headphones,
  Clock,
  Star,
} from "lucide-react";

// Company Info
const COMPANY_INFO = {
  phone: "44 35 30 00 30",
  whatsapp: "https://wa.me/message/TB7WYEMKU3L6G1",
};

// Turbo Packages
const TURBO_PACKAGES = [
  { speed: 100, price: 649, popular: false },
  { speed: 200, price: 990, popular: true },
  { speed: 500, price: 1249, popular: false },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const TurboPage = () => {
  const [hoveredPlan, setHoveredPlan] = useState(null);

  const features = [
    { icon: Gamepad2, title: "Gaming Sin Lag", description: "Latencia ultra baja para gaming competitivo" },
    { icon: Monitor, title: "Streaming 4K", description: "Múltiples dispositivos en calidad máxima" },
    { icon: Video, title: "Videollamadas HD", description: "Trabajo remoto sin interrupciones" },
    { icon: Cloud, title: "Descargas Rápidas", description: "Archivos pesados en segundos" },
    { icon: Shield, title: "Conexión Estable", description: "99.9% de disponibilidad garantizada" },
    { icon: Headphones, title: "Soporte Prioritario", description: "Atención preferencial 24/7" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a1a] text-white overflow-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/30 via-transparent to-transparent" />
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 149, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 149, 255, 0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[150px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-500/5 rounded-full blur-[200px]" />
      </div>

      {/* Navigation */}
      <nav className="relative z-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Volver al inicio</span>
            </Link>
            <a
              href={COMPANY_INFO.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all"
            >
              <Phone className="w-4 h-4" />
              <span>{COMPANY_INFO.phone}</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-12 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center mb-20"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-cyan-500/20 text-cyan-400 px-5 py-2 rounded-full text-sm font-semibold mb-8">
              <Zap className="w-4 h-4" />
              INTERNET ULTRAVELOZ
            </motion.div>
            
            <motion.h1
              variants={fadeInUp}
              className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6"
            >
              <span className="text-white">Flowing</span>{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500">
                Turbo
              </span>
            </motion.h1>
            
            <motion.p variants={fadeInUp} className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto mb-8">
              La nueva generación de internet por fibra óptica. 
              Velocidades extremas para los usuarios más exigentes.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2 text-cyan-400">
                <Check className="w-5 h-5" />
                <span>Hasta 500 Mbps</span>
              </div>
              <div className="flex items-center gap-2 text-cyan-400">
                <Check className="w-5 h-5" />
                <span>Latencia Ultra Baja</span>
              </div>
              <div className="flex items-center gap-2 text-cyan-400">
                <Check className="w-5 h-5" />
                <span>Soporte Prioritario</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Pricing Cards */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {TURBO_PACKAGES.map((pkg, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                onMouseEnter={() => setHoveredPlan(index)}
                onMouseLeave={() => setHoveredPlan(null)}
                className={`relative group ${pkg.popular ? 'md:-mt-8 md:mb-8' : ''}`}
              >
                {/* Glow effect */}
                <div className={`absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 rounded-3xl blur transition-opacity duration-500 ${hoveredPlan === index ? 'opacity-60' : 'opacity-20'}`} />
                
                <div className={`relative bg-[#0f0f2a]/90 backdrop-blur rounded-3xl p-8 border transition-all duration-500 ${pkg.popular ? 'border-cyan-500/50' : 'border-blue-500/20 hover:border-cyan-500/30'}`}>
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-1.5 rounded-full text-sm font-bold flex items-center gap-2">
                      <Star className="w-4 h-4" />
                      MÁS POPULAR
                    </div>
                  )}
                  
                  <div className="text-center">
                    {/* Speed indicator */}
                    <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-3xl mb-8 group-hover:scale-110 transition-transform duration-300">
                      <Gauge className="w-12 h-12 text-cyan-400" />
                    </div>
                    
                    <div className="text-7xl font-bold text-white mb-2">{pkg.speed}</div>
                    <div className="inline-block bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-5 py-1.5 rounded-lg text-sm font-bold mb-8">
                      MEGAS
                    </div>
                    
                    <div className="mb-10">
                      <span className="text-lg text-gray-400">Sólo</span>
                      <span className="text-5xl font-bold text-white mx-2">${pkg.price}</span>
                      <span className="text-gray-400">/mes</span>
                    </div>
                    
                    <ul className="text-left space-y-4 mb-10">
                      <li className="flex items-center gap-3 text-gray-300">
                        <div className="w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4 text-cyan-400" />
                        </div>
                        Latencia ultra baja
                      </li>
                      <li className="flex items-center gap-3 text-gray-300">
                        <div className="w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4 text-cyan-400" />
                        </div>
                        Ideal para gaming y streaming 4K
                      </li>
                      <li className="flex items-center gap-3 text-gray-300">
                        <div className="w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4 text-cyan-400" />
                        </div>
                        Soporte técnico prioritario
                      </li>
                      <li className="flex items-center gap-3 text-gray-300">
                        <div className="w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4 text-cyan-400" />
                        </div>
                        Instalación express
                      </li>
                    </ul>
                    
                    <a
                      href={COMPANY_INFO.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full py-4 rounded-full font-bold text-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:shadow-xl hover:shadow-cyan-500/30 transition-all"
                    >
                      ¡Lo Quiero!
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="text-center text-gray-500 mt-10"
          >
            * Todos los precios incluyen IVA. Consulta disponibilidad en tu zona.
          </motion.p>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-24 border-t border-blue-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold mb-4">
              ¿Por qué elegir <span className="text-cyan-400">Turbo</span>?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-gray-400 max-w-2xl mx-auto">
              Internet diseñado para quienes exigen el máximo rendimiento
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 hover:border-cyan-500/30 transition-colors group"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-7 h-7 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="relative py-24 border-t border-blue-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold mb-4">
              Ideal para
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-4 gap-6"
          >
            {[
              { emoji: "🎮", title: "Gamers", desc: "Competitivos y streamers" },
              { emoji: "🏠", title: "Home Office", desc: "Trabajo remoto sin límites" },
              { emoji: "👨‍👩‍👧‍👦", title: "Familias", desc: "Múltiples dispositivos" },
              { emoji: "📹", title: "Creadores", desc: "Contenido en alta calidad" },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center p-8 bg-gradient-to-b from-white/5 to-transparent rounded-2xl border border-white/10"
              >
                <div className="text-5xl mb-4">{item.emoji}</div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              ¿Listo para la <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">velocidad máxima</span>?
            </h2>
            <p className="text-xl text-gray-400 mb-10">
              Contáctanos ahora y únete a la nueva era del internet
            </p>
            <a
              href={COMPANY_INFO.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-10 py-5 rounded-full font-bold text-xl hover:shadow-xl hover:shadow-cyan-500/30 transition-all"
            >
              <Phone className="w-6 h-6" />
              Contactar por WhatsApp
            </a>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-8 border-t border-blue-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-gray-500 text-sm">
            <div className="flex items-center gap-3">
              <img 
                src="/logo-flowing.png" 
                alt="Flowing" 
                className="h-8 w-auto"
              />
              <span className="text-cyan-400">Turbo</span>
            </div>
            <Link to="/" className="hover:text-cyan-400 transition-colors">
              ← Volver a Flowing.lat
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TurboPage;
